import * as React from 'react'
import { useState, useRef, useEffect } from 'react'
import { Settings, FileUp, LogOut, User, ChevronDown } from 'lucide-react'
import { useAuth } from '@/features/auth/AuthContext'
import { AppSwitcher } from '@/components/layout/AppSwitcher'
import { useFacilityFilter } from '@/store/facilityFilterStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { listFacilities } from '@/features/facilities/api'
import { getFacilitySchedule } from '@/features/schedule/api'
import './TopBar.css'

interface Props {
  onOpenSchedule: () => void
  onOpenFailed: () => void
  onOpenImport: () => void
  onOpenProfile: () => void
  failedCount?: number
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  const first = parts[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : ''
  if (!last) return first.charAt(0).toUpperCase()
  return (first.charAt(0) + last.charAt(0)).toUpperCase()
}

function getFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name
}

function roleLabel(role: string | undefined): string {
  switch (role) {
    case 'THINKADMIN': return 'Think Admin'
    case 'SUPER_ADMIN': return 'Super Admin'
    case 'OPERATION_MANAGER': return 'Operation Manager'
    case 'SALES_DIRECTOR': return 'Sales Director'
    case 'NURSE': return 'Nurse'
    case 'FRONT_DESK': return 'Front Desk'
    case 'VIEWER': return 'Viewer'
    default: return role ?? ''
  }
}

function formatDisplayTime(hour: number, min: number): string {
  const ampm = hour < 12 ? 'AM' : 'PM'
  const h = hour % 12 === 0 ? 12 : hour % 12
  const m = min.toString().padStart(2, '0')
  return `${h}:${m} ${ampm}`
}

export function TopBar({
  onOpenSchedule,
  onOpenFailed,
  onOpenImport,
  onOpenProfile,
  failedCount = 0,
}: Props): React.JSX.Element {
  const { user, logout, hasRole } = useAuth()
  const queryClient = useQueryClient()

  const [menuOpen, setMenuOpen] = useState(false)
  const [facilityOpen, setFacilityOpen] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)
  const facilityRef = useRef<HTMLDivElement>(null)

  const { facilityId, setFacility } = useFacilityFilter()
  const isSuperAdmin = hasRole('SUPER_ADMIN', 'THINKADMIN')

  const { data: facilities = [] } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: listFacilities,
    enabled: !!user,
    staleTime: 60_000,
  })

  // Facility-scoped staff: backend uses their own facility (pass null).
  // Super admin: use the facility selected in the switcher.
  const scheduleFacilityId = isSuperAdmin ? facilityId : null
  const { data: schedule } = useQuery({
    queryKey: ['schedule', 'facility', scheduleFacilityId],
    queryFn: () => getFacilitySchedule(scheduleFacilityId),
    enabled: !!user,
    staleTime: 60_000,
  })

  const selectedFacility = isSuperAdmin
    ? facilities.find((f) => f.id === facilityId)
    : facilities.find((f) => f.id === user?.facility_id)
  const facilityLabel = isSuperAdmin
    ? (selectedFacility?.name ?? 'All Facilities')
    : (selectedFacility?.name ?? user?.name ?? '')

  // Schedule status for center bar
  const scheduleStatus = React.useMemo(() => {
    if (!schedule?.active) return null
    const cadenceStr = schedule.cadence === 'daily' ? 'daily' : schedule.cadence === 'weekly' ? 'weekly' : 'monthly'
    const timeStr = formatDisplayTime(schedule.send_hour_local, schedule.send_minute_local)
    return { cadenceStr, timeStr }
  }, [schedule])

  // Close user menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return undefined
    function onClick(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  // Close facility dropdown on outside click / Escape
  useEffect(() => {
    if (!facilityOpen) return undefined
    function onClick(e: MouseEvent): void {
      if (facilityRef.current && !facilityRef.current.contains(e.target as Node)) {
        setFacilityOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setFacilityOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [facilityOpen])

  function handleSelectFacility(id: string | null): void {
    setFacility(id)
    setFacilityOpen(false)
    void queryClient.invalidateQueries({ queryKey: ['residents'] })
  }

  const initials = user ? getInitials(user.name) : '?'
  const firstName = user ? getFirstName(user.name) : 'User'

  return (
    <header className="topbar">
      {/* Left: logo + separator + facility */}
      <div className="topbar-left">
        <img src="/vitals-logo.svg" alt="eCareLTC Vitals" className="topbar-logo" />
        <span className="topbar-brand-sep" aria-hidden="true" />

        <div className="topbar-facility-wrap" ref={facilityRef}>
          {isSuperAdmin ? (
            <button
              type="button"
              className="topbar-facility-btn"
              onClick={() => setFacilityOpen((v) => !v)}
              aria-expanded={facilityOpen}
              aria-haspopup="listbox"
            >
              {facilityLabel}
              <ChevronDown size={13} strokeWidth={2} />
            </button>
          ) : (
            <span className="topbar-facility-static">{facilityLabel}</span>
          )}
          {isSuperAdmin && facilityOpen && (
            <div className="topbar-facility-dropdown" role="listbox">
              <button
                type="button"
                className={`topbar-facility-option${facilityId === null ? ' topbar-facility-option--active' : ''}`}
                onClick={() => handleSelectFacility(null)}
              >
                All Facilities
              </button>
              {facilities.length > 0 && <div className="topbar-facility-divider" />}
              {facilities.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`topbar-facility-option${facilityId === f.id ? ' topbar-facility-option--active' : ''}`}
                  onClick={() => handleSelectFacility(f.id)}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Center: schedule status */}
      <div className="topbar-center">
        <span className="topbar-schedule-status">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          {scheduleStatus ? (
            <>
              Vitals are scheduled to send{' '}
              <strong>{scheduleStatus.cadenceStr}</strong>{' '}
              at <strong>{scheduleStatus.timeStr}</strong>{' '}
              via fax.{' '}
            </>
          ) : (
            <>Vitals aren&apos;t scheduled yet.{' '}</>
          )}
          <button type="button" className="topbar-schedule-change" onClick={onOpenSchedule}>
            {scheduleStatus ? 'Change' : 'Set up'}
          </button>
        </span>
      </div>

      {/* Right: queues · sep · user menu · sep · app switcher */}
      <div className="topbar-right">
        {/* Queues button */}
        <button type="button" className="topbar-queues-btn" onClick={onOpenFailed}>
          <span className="topbar-q-dot" data-alert={failedCount > 0 ? 'true' : 'false'} />
          Queues ( {failedCount} )
        </button>

        <span className="topbar-sep" aria-hidden="true" />

        {/* User trigger + dropdown */}
        <div className="topbar-user-wrap" ref={menuRef}>
          <button
            type="button"
            className="topbar-user-trigger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <span className="topbar-user-avatar">{initials}</span>
            <span className="topbar-user-meta">
              <span className="topbar-user-name">{user?.name ?? firstName}</span>
              <span className="topbar-user-role">{roleLabel(user?.role)}</span>
            </span>
            <ChevronDown size={13} strokeWidth={2} className="topbar-user-chevron" />
          </button>

          {menuOpen && (
            <div className="topbar-menu" role="menu">
              <button
                type="button"
                className="topbar-menu-item"
                onClick={() => { setMenuOpen(false); onOpenProfile() }}
              >
                <User size={15} strokeWidth={1.8} />
                Profile
              </button>
              <button
                type="button"
                className="topbar-menu-item"
                onClick={() => { setMenuOpen(false); onOpenSchedule() }}
              >
                <Settings size={15} strokeWidth={1.8} />
                Schedule settings
              </button>
              <button
                type="button"
                className="topbar-menu-item"
                onClick={() => { setMenuOpen(false); onOpenImport() }}
              >
                <FileUp size={15} strokeWidth={1.8} />
                Import residents (CSV)
              </button>

              <div className="topbar-menu-divider" />

              <button
                type="button"
                className="topbar-menu-item topbar-menu-danger"
                onClick={() => void logout()}
              >
                <LogOut size={15} strokeWidth={1.8} />
                Logout
              </button>
            </div>
          )}
        </div>

        <span className="topbar-sep" aria-hidden="true" />

        <AppSwitcher />
      </div>
    </header>
  )
}
